import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  TableFooter,
  Button,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  LinearProgress,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import * as XLSX from "xlsx";
import {
  extractDate,
  extractTime,
  getDbHeader,
  toastError,
} from "../../utils/util";
import useCommonGetInit from "../../hooks/useCommonGetInit";
import CustomCircularP from "../../widgets/CustomCircularP";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import axios from "axios";
import { getOnsiteHeader } from "./utils";
import MuPb from "../../widgets/MuPb";
import { createEventDays } from "./eventDays";

export { createEventDays } from "./eventDays";

export default function DaywiseFootfall() {
  const { id } = useParams();
  const {
    data: Mdata,
    isLoading: mLoading,
    isError,
    error,
  } = useCommonGetInit(`${API_ENDPOINT}/user/check-onsite?id=${id}`);

  const [isLoading, setIsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [eventDateRange, setEventDateRange] = useState(undefined);
  const [eventDatesError, setEventDatesError] = useState("");
  const [eventDatesLoading, setEventDatesLoading] = useState(true);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [fdata, setFdata] = useState([]);
  const navigate = useNavigate();

  const eventDays = useMemo(
    () =>
      eventDateRange
        ? createEventDays(
            eventDateRange.eventStartDate,
            eventDateRange.eventEndDate
          )
        : [],
    [eventDateRange]
  );

  const [open, setOpen] = useState(false);
  const [inputCode, setInputCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const CORRECT_CODE = "RTYIOGF456$%";

  const handleOpenDialog = () => {
    setErrorMessage("");
    setInputCode("");
    setOpen(true);
  };

  async function loadData() {
    try {
      setIsLoading(true);
      const { data: MyData } = await axios.get(
        `${API_ENDPOINT}/onsite/scanned-users-footfall-medium`,
        getOnsiteHeader(ONSITE_MODULE_ITEMS.FOOTFALL_ANALYSIS)
      );
      setFdata(MyData);
    } catch (error) {
      console.log(error);
      //toastError(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadEventDates() {
    try {
      setEventDatesLoading(true);
      setEventDatesError("");

      const { data } = await axios.get(
        `${API_ENDPOINT}/event/get-event-dates`,
        getDbHeader()
      );
      const days = createEventDays(data?.eventStartDate, data?.eventEndDate);

      setEventDateRange(
        days.length > 0
          ? {
              eventStartDate: data.eventStartDate,
              eventEndDate: data.eventEndDate,
            }
          : null
      );
    } catch (error) {
      console.log(error);
      setEventDateRange(undefined);
      setEventDatesError("Unable to load event dates. Please try again.");
    } finally {
      setEventDatesLoading(false);
    }
  }

  const handleConfirm = () => {
    if (inputCode === CORRECT_CODE) {
      exportAuditReport();
      setOpen(false);
    } else {
      setErrorMessage("Incorrect code. Please try again!");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setErrorMessage("");
  };

  useEffect(() => {
    loadEventDates();

    const interval = setInterval(() => {
      loadData();
    }, 120000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (Mdata) {
      // Logic for Mdata can be added back if needed
    }
    loadData();
  }, [Mdata]);

  const rows = useMemo(() => {
    if (eventDays.length === 0) return [];

    const rolesMapping = {
      VIP: "vip",
      "Special Invitee": "vip",

      MEDIA: "media",

      STUDENT: "students",
      student: "students",
      BULK_STUDENT: "students",

      VISITOR: "visitor",
      visitor: "visitor",

      DELEGATION: "delegation",
      TRADE_DELEGATE: "delegation",
      trade_delegate: "delegation",

      EC: "ec",
    };

    const createInitialCounts = () => {
      const template = {
        vip: 0,
        media: 0,
        students: 0,
        delegation: 0,
        ec: 0,
        preEventReg: 0,
        eventDaysReg: 0,
        totalVisitors: 0,
        repeatVisitors: 0,
        total: 0,
      };
      const counts = {};
      eventDays.forEach(({ key }) => {
        counts[key] = { ...template };
      });
      return counts;
    };

    const initialCounts = createInitialCounts();

    const visitorCheck = {
      vip: {},
      media: {},
      students: {},
      delegation: {},
      ec: {},
      visitor: {},
      preEventReg: {},
      eventDaysReg: {},
    };

    fdata.forEach((user) => {
      const mappedRole = rolesMapping[user.role];
      if (!mappedRole) return;

      const registrationDate = new Date(user.createdOn);
      const isPreEventReg = registrationDate < eventDays[0].date;

      eventDays.forEach(({ key }) => {
        if (user[key] && initialCounts[key]) {
          if (!visitorCheck[mappedRole][user.barcode_id]) {
            initialCounts[key][mappedRole] += 1;
            visitorCheck[mappedRole][user.barcode_id] = [key];
          } else {
            if (!visitorCheck[mappedRole][user.barcode_id].includes(key)) {
              visitorCheck[mappedRole][user.barcode_id].push(key);
              initialCounts[key].repeatVisitors += 1;
            }
          }

          if (mappedRole === "visitor") {
            const regRole = isPreEventReg ? "preEventReg" : "eventDaysReg";
            if (!visitorCheck[regRole][user.barcode_id]) {
              initialCounts[key][regRole] += 1;
              visitorCheck[regRole][user.barcode_id] = [key];
            } else {
              if (!visitorCheck[regRole][user.barcode_id].includes(key)) {
                visitorCheck[regRole][user.barcode_id].push(key);
              }
            }
          }
        }
      });
    });

    // Calculate totals for each day
    eventDays.forEach(({ key }) => {
      const counts = initialCounts[key];
      counts.totalVisitors =
        counts.vip +
        counts.media +
        counts.students +
        counts.delegation +
        counts.ec +
        counts.preEventReg +
        counts.eventDaysReg;
      counts.total = counts.totalVisitors + counts.repeatVisitors;
    });

    // Convert days to actual dates
    const rowsData = eventDays.map(({ key, date }) => {
      const dayDateString = date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });

      return {
        day: dayDateString,
        ...initialCounts[key],
      };
    });

    return rowsData;
  }, [eventDays, fdata]);

  const columnSums = useMemo(() => {
    return rows.reduce(
      (totals, row) => ({
        vip: totals.vip + (row.vip || 0),
        media: totals.media + (row.media || 0),
        students: totals.students + (row.students || 0),
        delegation: totals.delegation + (row.delegation || 0),
        ec: totals.ec + (row.ec || 0),
        preEventReg: totals.preEventReg + (row.preEventReg || 0),
        eventDaysReg: totals.eventDaysReg + (row.eventDaysReg || 0),
        totalVisitors: totals.totalVisitors + (row.totalVisitors || 0),
        repeatVisitors: totals.repeatVisitors + (row.repeatVisitors || 0),
        total: totals.total + (row.total || 0),
      }),
      {
        vip: 0,
        media: 0,
        students: 0,
        delegation: 0,
        ec: 0,
        preEventReg: 0,
        eventDaysReg: 0,
        totalVisitors: 0,
        repeatVisitors: 0,
        total: 0,
      }
    );
  }, [rows]);

  function transformFootfallData(items) {
    if (!items || !Array.isArray(items)) return [];
    const transformedList = [];

    items.forEach((user) => {
      const { role } = user;
      const rest = Object.fromEntries(
        Object.entries(user).filter(
          ([key]) => key !== "role" && !/^day\d+(timestamp)?$/.test(key)
        )
      );

      let entryCount = 0;

      const addEntry = (timestamp) => {
        if (!timestamp) return;

        const currentRole = entryCount > 0 ? `${role} (repeat)` : role;
        transformedList.push({
          ...rest,
          role: currentRole,
          Day: extractDate(timestamp),
          Time: extractTime(timestamp),
        });
        entryCount++;
      };

      eventDays.forEach(({ key }) => {
        if (user[key] && user[`${key}timestamp`]) {
          addEntry(user[`${key}timestamp`]);
        }
      });
    });

    return transformedList;
  }

  function getFinalUfiUserType(userType) {
    if (userType.toLowerCase().includes("repeat")) {
      return "Repeat Visitor";
    } else if (userType.toLowerCase() === "vip") {
      return "Special Invitee";
    } else {
      return userType
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    }
  }

  const exportAuditReport = async () => {
    try {
      setDownloading(true);
      const { data: MyData } = await axios.get(
        `${API_ENDPOINT}/onsite/scanned-users-footfall-full`,
        getOnsiteHeader(ONSITE_MODULE_ITEMS.FOOTFALL_ANALYSIS)
      );
      const result = transformFootfallData(MyData);
      const filteredData = result
        .filter(
          (item) =>
            !item.role.includes("EXHIBITOR") && !item.role.includes("SERVICE")
        )
        .map((item) => {
          const {
            _id,
            user_id,
            barcode_id,
            visitorGroup,
            title,
            name,
            firstName,
            lastName,
            mobileNo,
            email,
            designation,
            department,
            education,
            address,
            companyName,
            instituteName,
            role: user_type,
            country,
            state,
            city,
            pinzip,
            howDidYouFindOutAboutThisEvent,
            industrySector,
            qrLink,
            createdOn,
            Day,
            Time,
          } = item;

          return {
            "Object Id": _id,
            "User ID": user_id,
            "Barcode ID": barcode_id,
            "User Type": user_type,
            "Final UFI User Type": getFinalUfiUserType(user_type),
            "Visitor Group": visitorGroup,
            Title: title,
            "First Name": firstName,
            "Last Name": lastName,
            name: name,
            "Phone Number": mobileNo,
            Email: email,
            Designation: designation,
            Department: department,
            Education: education,
            Address: address,
            PinCode: pinzip,
            "Company/Institute Name": `${companyName ? companyName : ""}${
              instituteName ? instituteName : ""
            }`,
            Country: country?.name,
            State: state?.name,
            City: city?.name,
            "How Found Out": howDidYouFindOutAboutThisEvent,
            "Industry Sector": industrySector,
            "QR Link": qrLink,
            "Registration Date": extractDate(createdOn),
            "Registration Time": extractTime(createdOn),
            Day: Day,
            Time: Time,
          };
        });

      const ws = XLSX.utils.json_to_sheet(filteredData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "IMTEX footfall report");
      XLSX.writeFile(wb, "imtex_report.xlsx");
    } catch (error) {
      toastError(error);
    } finally {
      setDownloading(false);
    }
  };

  if (mLoading || eventDatesLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{error?.message}</Alert>;
  }
  if (eventDatesError) {
    return <Alert severity="error">{eventDatesError}</Alert>;
  }
  if (!eventDateRange || eventDays.length === 0) {
    return (
      <Alert severity="warning">Event dates are not set for this event.</Alert>
    );
  }

  return (
    <Box>
      {downloading && <LinearProgress />}
      <CustomCircularP show={isLoading} />
      <Box p={2} flex={1}>
        <Box
          display={"flex"}
          flexDirection={isSmallScreen ? "column" : "row"}
          alignItems="center"
        >
          <Typography
            variant="h5"
            fontWeight={600}
            style={{ flex: 1, textAlign: isSmallScreen ? "center" : "left" }}
          >
            Footfall Report
          </Typography>

          <Button
            sx={{
              alignSelf: isSmallScreen ? "center" : "flex-end",
              mt: isSmallScreen ? 2 : 0,
              mr: 2,
            }}
            onClick={() => {
              navigate(`/onsite/daywise-footfall`);
            }}
            size="small"
            variant="outlined"
          >
            TimeWise Footfall report
          </Button>
          <Button
            sx={{
              alignSelf: isSmallScreen ? "center" : "flex-end",
              mt: isSmallScreen ? 2 : 0,
              mr: 2,
            }}
            onClick={() => {
              navigate(`/onsite/footfall-comparison`);
            }}
            size="small"
            variant="outlined"
          >
            Comparison
          </Button>

          {/* Replaced Button with IconButton */}
          <IconButton
            sx={{
              alignSelf: isSmallScreen ? "center" : "flex-end",
              mt: isSmallScreen ? 2 : 0,
              mr: 1, // Added right margin to space it from the download button
            }}
            onClick={() => {
              loadData();
              loadEventDates();
            }}
            size="small"
            color="primary"
          >
            <RefreshIcon />
          </IconButton>

          <IconButton size="small" onClick={handleOpenDialog}>
            <GetAppOutlinedIcon />
          </IconButton>

          <Dialog open={open} onClose={handleClose}>
            <DialogTitle>Enter Download Code</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                id="download-code"
                label="Download Code"
                type="text"
                fullWidth
                variant="standard"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                error={Boolean(errorMessage)}
                helperText={errorMessage}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleConfirm} variant="contained">
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell align="right">Special Invitee</TableCell>
                <TableCell align="right">Media</TableCell>
                <TableCell align="right">Students</TableCell>
                <TableCell align="right">Delegation</TableCell>
                <TableCell align="right">EC</TableCell>
                <TableCell align="right">Pre-Event visitor Reg.</TableCell>
                <TableCell align="right">Event Day visitor Reg.</TableCell>
                <TableCell align="right">
                  Total Visitors (excl. repeat)
                </TableCell>
                <TableCell align="right">Repeats</TableCell>
                <TableCell align="right">Total (incl. repeat)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.day}>
                  <TableCell component="th" scope="row">
                    {row.day}
                  </TableCell>
                  <TableCell align="right">{row.vip}</TableCell>
                  <TableCell align="right">{row.media}</TableCell>
                  <TableCell align="right">{row.students}</TableCell>
                  <TableCell align="right">{row.delegation}</TableCell>
                  <TableCell align="right">{row.ec}</TableCell>
                  <TableCell align="right">{row.preEventReg}</TableCell>
                  <TableCell align="right">{row.eventDaysReg}</TableCell>
                  <TableCell align="right">{row.totalVisitors}</TableCell>
                  <TableCell align="right">{row.repeatVisitors}</TableCell>
                  <TableCell align="right">{row.total}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell sx={{ fontSize: "16px" }} component="th" scope="row">
                  Totals
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.vip}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.media}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.students}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.delegation}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.ec}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.preEventReg}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.eventDaysReg}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.totalVisitors}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.repeatVisitors}
                </TableCell>
                <TableCell sx={{ fontSize: "16px" }} align="right">
                  {columnSums.total}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
